import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface MediaItem {
  url: string
  key?: string
  type: 'image' | 'video' | 'pdf'
  name: string
  size?: number
}

export interface ResearchNote {
  id: string
  content: string
  media: MediaItem[]
  tags: string[]
  pinned: boolean
  author: string
  authorId: string
  createdAt: string
  updatedAt: string
}

interface NotesResponse {
  notes: ResearchNote[]
  tags: string[]
  total: number
  hasMore: boolean
  page: number
}

export function useResearchNotes(params?: { tag?: string; search?: string; page?: number }) {
  const qs = new URLSearchParams()
  if (params?.tag) qs.set('tag', params.tag)
  if (params?.search) qs.set('search', params.search)
  if (params?.page && params.page > 1) qs.set('page', String(params.page))
  const query = qs.toString()

  return useQuery<NotesResponse>({
    queryKey: ['research-notes', params?.tag, params?.search, params?.page],
    queryFn: () => api.get(`/research-notes${query ? `?${query}` : ''}`),
  })
}

export function useCreateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { content: string; tags?: string[]; pinned?: boolean }) =>
      api.post<{ id: string; ok: boolean; createdAt: string }>('/research-notes', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['research-notes'] })
    },
  })
}

export function useUpdateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; content?: string; tags?: string[]; pinned?: boolean }) =>
      api.put<{ ok: boolean }>(`/research-notes/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['research-notes'] })
    },
  })
}

export function useDeleteNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/research-notes/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['research-notes'] })
    },
  })
}

export function useUploadNoteMedia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, file }: { noteId: string; file: File }) =>
      api.uploadSingleWithProgress<{ ok: boolean; url: string; type: string; name: string }>(
        file,
        `/research-notes/${noteId}/media`
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['research-notes'] })
    },
  })
}

export function useDeleteNoteMedia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, mediaIndex }: { noteId: string; mediaIndex: number }) =>
      api.delete<{ ok: boolean }>(`/research-notes/${noteId}/media/${mediaIndex}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['research-notes'] })
    },
  })
}

export function useTogglePin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<{ ok: boolean; pinned: boolean }>(`/research-notes/${id}/pin`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['research-notes'] })
    },
  })
}
